/*
Copyright© Celligence International, LLC - 2024

This software program is protected by copyright laws and is the property of
Celligence International, LLC (Celligence). Any unauthorized reproduction,
distribution, modification, or public display of this program, without the
explicit permission of Celligence is strictly prohibited. If you have gained

access to this program without the expressed approval of Celligence report
the incident to legal@celligence.com.
*/

import { createContext, useCallback, useContext, useState } from 'react';
import useApplyAggregateEscrowAnalysis from '../common/hooks/useApplyAggregateEscrowAnalysis';
import { getEffectiveDateUTCTimestamp, useEscrowAnalysisCalculationContext } from './CalculateEscrowAnalysisContext';
import { isObjEmptyOrNull, isStrEmptyOrNull } from '../common/UtilityFunctions';
import { useLoanContext } from './LoanContext';

type ApplyAEAContextType = {
  applyingAEA: boolean;
  errorApplyingAEA: string;
  applyAEAClicked: boolean;
  setApplyAEAClicked: (val: boolean) => void;
  applyAEAResultRetrieved: boolean;
  setApplyAEAResultRetrieved: (val: boolean) => void;
  applyAEAResp: UnknownBatchEndpointResp;
  applyAEA: (params: ApplyAEAParams) => Promise<boolean>;
};

const ApplyAEAContext = createContext<ApplyAEAContextType>({
  applyingAEA: false,
  errorApplyingAEA: '',
  applyAEAClicked: false,
  setApplyAEAClicked: (val: boolean) => {},
  applyAEAResultRetrieved: false,
  setApplyAEAResultRetrieved: (val: boolean) => {},
  applyAEAResp: null,
  applyAEA: async (params: ApplyAEAParams) => false,
});

const useApplyAEAContext = () => {
  const applyAEAContext = useContext(ApplyAEAContext);
  if (applyAEAContext === undefined) {
    /// throwing this error helps in development when the context is out of scope.
    throw new Error('ApplyAEAContext must be inside a ApplyAEAProvider');
  }
  return applyAEAContext;
};

const ApplyAEAProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { applyingAEA, errorApplyingAEA, applyAEA, applyAEAResp } = useApplyAggregateEscrowAnalysis();

  const [applyAEAClicked, setApplyAEAClicked] = useState(false);
  const [applyAEAResultRetrieved, setApplyAEAResultRetrieved] = useState(false);

  return (
    <ApplyAEAContext.Provider
      value={{
        applyAEAClicked,
        setApplyAEAClicked,
        applyingAEA,
        errorApplyingAEA,
        applyAEAResultRetrieved,
        setApplyAEAResultRetrieved,
        applyAEAResp,
        applyAEA,
      }}
    >
      {children}
    </ApplyAEAContext.Provider>
  );
};

export { ApplyAEAProvider, useApplyAEAContext };
